/**
 * AssetLibraryPanel Component
 *
 * Side panel showing the asset library for drag & drop to channels.
 * NOT a modal/overlay - it sits beside the main content in a split layout.
 *
 * Features:
 * - Filter chips (All, AI, Upload, JTL, Reference, Unused)
 * - Search input
 * - Session-grouped asset grid
 * - Draggable items for cross-container DnD
 *
 * Built with plain Tailwind CSS (theme-ready).
 * Matches the HTML mock exactly.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X, Search, Images, ChevronDown, ChevronRight, ChevronLeft, LayoutGrid, List } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { getImageCollection } from '@/lib/api/image-collection';
import type { ProductImageAsset } from '@/types/image-collection';
import { cn } from '@/lib/utils';

type SourceFilter = 'all' | 'ai_generated' | 'user_upload' | 'jtl_original' | 'optimized';

interface AssetLibraryPanelProps {
  productId: string;
  onClose: () => void;
}

const FILTER_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'ai_generated', label: '✨ KI' },
  { value: 'user_upload', label: '☁️ Upload' },
  { value: 'jtl_original', label: '🔄 JTL' },
  { value: 'optimized', label: '⚡ Optimiert' },
];

export function AssetLibraryPanel({ productId, onClose }: AssetLibraryPanelProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [page, setPage] = useState(1);
  const [showGrouped, setShowGrouped] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(
    new Set(['today', 'yesterday'])
  );

  // Build filters for API
  const apiFilters = useMemo(() => {
    const filters: Record<string, string | undefined> = {
      page: page.toString(),
      per_page: '24',
    };

    if (sourceFilter !== 'all') {
      filters.source = sourceFilter;
    }

    return filters;
  }, [sourceFilter, page]);

  // Fetch assets
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['asset-library', productId, apiFilters],
    queryFn: () => getImageCollection(productId, apiFilters),
    enabled: !!productId,
    staleTime: 30000,
  });

  const assets = useMemo(() => data?.images || [], [data?.images]);
  const totalCount = data?.total_count || 0;
  const totalPages = data?.total_pages || 1;

  // Deduplicate by content_hash (same image linked to multiple channels)
  const uniqueAssets = useMemo(() => {
    const seen = new Set<string>();
    return assets.filter((asset) => {
      const key = asset.content_hash || asset.asset_id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [assets]);

  // Filter by search query (client-side)
  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return uniqueAssets;

    const query = searchQuery.toLowerCase();
    return uniqueAssets.filter((asset) => {
      const altText = asset.alt_text?.toLowerCase() || '';
      const tags = asset.tags?.map((t) => t.name.toLowerCase()).join(' ') || '';
      return altText.includes(query) || tags.includes(query);
    });
  }, [uniqueAssets, searchQuery]);

  // Group assets by session/date
  const groupedAssets = useMemo(() => {
    const groups: Map<
      string,
      { dateLabel: string; timeLabel: string; sessionLabel: string; assets: ProductImageAsset[] }
    > = new Map();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const asset of filteredAssets) {
      const createdAt = new Date(asset.created_at);
      const dateOnly = new Date(createdAt);
      dateOnly.setHours(0, 0, 0, 0);

      let groupKey: string;
      let dateLabel: string;
      const timeLabel = createdAt.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      });

      if (dateOnly.getTime() === today.getTime()) {
        groupKey = 'today';
        dateLabel = 'Heute';
      } else if (dateOnly.getTime() === yesterday.getTime()) {
        groupKey = 'yesterday';
        dateLabel = 'Gestern';
      } else {
        groupKey = dateOnly.toISOString().split('T')[0];
        dateLabel = dateOnly.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }

      const sessionLabel = getSessionLabel(asset.upload_source);

      if (!groups.has(groupKey)) {
        groups.set(groupKey, { dateLabel, timeLabel, sessionLabel, assets: [] });
      }
      groups.get(groupKey)!.assets.push(asset);
    }

    return groups;
  }, [filteredAssets]);

  const toggleSession = useCallback((sessionKey: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionKey)) {
        next.delete(sessionKey);
      } else {
        next.add(sessionKey);
      }
      return next;
    });
  }, []);

  const handleSourceFilterChange = useCallback((filter: SourceFilter) => {
    setSourceFilter(filter);
    setPage(1);
  }, []);

  // Close on Escape (standard UX for slide-in panels)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <aside
      className="fixed right-0 top-16 bottom-0 w-1/4 min-w-[400px] max-w-[500px] border-l border-neutral-800 flex flex-col z-40"
      style={{
        background: 'rgba(23, 23, 23, 0.98)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <Images className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Asset Library</h2>
            <p className="text-sm text-neutral-500">Drag → Kanal</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded hover:bg-neutral-800 flex items-center justify-center text-neutral-500 hover:text-white transition-all"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search */}
      <div className="px-5 py-3 border-b border-neutral-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-neutral-800 border border-neutral-700 text-sm text-white placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Filter Chips */}
      <div className="px-5 py-3 border-b border-neutral-800">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleSourceFilterChange(filter.value)}
                className={cn(
                  'px-3 py-1.5 rounded text-sm font-medium transition-all',
                  sourceFilter === filter.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white'
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {/* View Toggle */}
          <div className="flex rounded overflow-hidden border border-neutral-700 flex-shrink-0">
            <button
              onClick={() => setShowGrouped(true)}
              className={cn(
                'p-2 transition-all',
                showGrouped
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white'
              )}
              title="Nach Datum gruppieren"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowGrouped(false)}
              className={cn(
                'p-2 transition-all',
                !showGrouped
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white'
              )}
              title="Alle anzeigen"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Asset Grid */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
          </div>
        )}

        {!isLoading && filteredAssets.length === 0 && (
          <div className="text-center py-12 text-sm text-neutral-500">Keine Assets gefunden</div>
        )}

        {!isLoading && filteredAssets.length > 0 && showGrouped && (
          <div key="grouped-view" className="space-y-5">
            {Array.from(groupedAssets.entries()).map(([key, group]) => (
              <SessionGroup
                key={key}
                sessionKey={key}
                dateLabel={group.dateLabel}
                timeLabel={group.timeLabel}
                sessionLabel={group.sessionLabel}
                assets={group.assets}
                isExpanded={expandedSessions.has(key)}
                onToggle={() => toggleSession(key)}
                productId={productId}
              />
            ))}
          </div>
        )}

        {!isLoading && filteredAssets.length > 0 && !showGrouped && (
          <div key="flat-view" className="grid grid-cols-3 gap-2">
            {filteredAssets.map((asset) => (
              <DraggableAssetThumbnail key={asset.asset_id} asset={asset} productId={productId} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      <div className="px-5 py-3 border-t border-neutral-800 flex items-center justify-between">
        <span className="text-sm text-neutral-500">{totalCount} Assets</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isFetching}
            className="w-8 h-8 rounded bg-neutral-800 text-neutral-500 hover:bg-neutral-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-neutral-500 px-2">
            {page}/{totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || isFetching}
            className="w-8 h-8 rounded bg-neutral-800 text-neutral-500 hover:bg-neutral-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

// =============================================================================
// SessionGroup Component
// =============================================================================

interface SessionGroupProps {
  sessionKey: string;
  dateLabel: string;
  timeLabel: string;
  sessionLabel: string;
  assets: ProductImageAsset[];
  isExpanded: boolean;
  onToggle: () => void;
  productId: string;
}

function SessionGroup({
  sessionKey: _sessionKey,
  dateLabel,
  timeLabel,
  sessionLabel,
  assets,
  isExpanded,
  onToggle,
  productId,
}: SessionGroupProps) {
  return (
    <div className={cn(!isExpanded && 'opacity-50 hover:opacity-100 transition-opacity')}>
      <button onClick={onToggle} className="flex items-center gap-2 w-full text-left mb-3 group">
        <ChevronDown
          className={cn(
            'w-4 h-4 text-neutral-500 group-hover:text-primary-400 transition-all',
            !isExpanded && '-rotate-90'
          )}
        />
        <div className="flex-1">
          <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">
            {dateLabel} {timeLabel && dateLabel !== 'Gestern' ? timeLabel : ''}
          </span>
          <p
            className={cn(
              'text-sm font-medium',
              isExpanded ? 'text-white' : 'text-neutral-400'
            )}
          >
            {sessionLabel}
          </p>
        </div>
        <span className="text-sm text-neutral-600 bg-neutral-800 px-2 py-1 rounded">
          {assets.length}
        </span>
      </button>

      {isExpanded && (
        <div className="grid grid-cols-3 gap-2">
          {assets.map((asset) => (
            <DraggableAssetThumbnail key={asset.asset_id} asset={asset} productId={productId} />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DraggableAssetThumbnail Component
// =============================================================================

interface DraggableAssetThumbnailProps {
  asset: ProductImageAsset;
  productId: string;
}

function DraggableAssetThumbnail({ asset, productId: _productId }: DraggableAssetThumbnailProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `library-${asset.asset_id}`,
    data: {
      type: 'library-asset',
      asset: {
        asset_id: asset.asset_id,
        link_id: `library-${asset.asset_id}`,
        channel_id: null,
        channel_name: 'Library',
        channel_type: null,
        slot: 0,
        sort_number: 0,
        alt_text: asset.alt_text,
        s3_key_original: null,
        s3_url: asset.s3_url,
        width: asset.width,
        height: asset.height,
        mime_type: null,
        content_hash: null,
        upload_source: asset.upload_source,
      },
      channelKey: 'library',
      channelId: null,
      fromLibrary: true,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const imageUrl = `/api/v1/asset/${asset.asset_id}/image?size=thumbnail`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group relative aspect-square rounded border overflow-hidden cursor-grab',
        'border-neutral-700 bg-neutral-800',
        'hover:border-primary-500 hover:scale-105 transition-all',
        isDragging && 'ring-2 ring-primary-500'
      )}
    >
      <img
        src={imageUrl}
        alt={asset.alt_text || 'Asset'}
        className="w-full h-full object-cover"
        loading="lazy"
        draggable={false}
      />

      {/* Source Badge */}
      <span
        className="absolute bottom-1 left-1 text-sm opacity-60 group-hover:opacity-100 transition-opacity"
        title={getSourceLabel(asset.upload_source)}
      >
        {getSourceEmoji(asset.upload_source)}
      </span>
    </div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function getSourceLabel(source: string | null): string {
  switch (source) {
    case 'ai_generated':
    case 'ai':
    case 'generated':
      return 'KI-generiert';
    case 'user_upload':
    case 'upload':
    case 'user':
      return 'Upload';
    case 'jtl_original':
    case 'integration_sync':
    case 'jtl':
    case 'sync':
      return 'JTL Import';
    case 'optimized':
      return 'Optimiert';
    default:
      return 'Unbekannt';
  }
}

function getSessionLabel(source: string | null): string {
  switch (source) {
    case 'ai_generated':
    case 'ai':
    case 'generated':
      return '✨ KI-Session';
    case 'user_upload':
    case 'upload':
    case 'user':
      return '☁️ Uploads';
    case 'jtl_original':
    case 'integration_sync':
    case 'jtl':
    case 'sync':
      return '🔄 JTL Import';
    case 'optimized':
      return '⚡ Optimiert';
    default:
      return '📷 Assets';
  }
}

function getSourceEmoji(source: string | null): string {
  switch (source) {
    case 'ai_generated':
    case 'ai':
    case 'generated':
      return '✨';
    case 'user_upload':
    case 'upload':
    case 'user':
      return '☁️';
    case 'jtl_original':
    case 'integration_sync':
    case 'jtl':
    case 'sync':
      return '🔄';
    case 'optimized':
      return '⚡';
    default:
      return '📷';
  }
}
